import {
  DEFAULT_RULE,
  type Aircraft,
  type AirportCapability,
  type AllowRow,
  type AllowState,
  type CapabilityException,
  type CityPair,
  type CityPairCapability,
  type CountryCapability,
  type ExceptionScope,
  type FleetColumn,
} from './types'

/**
 * Fleet permission resolution.
 *
 * Two tiers decide whether a tail may work a leg, and both have to pass:
 *
 *   1. Aircraft tier — the operator's three matrices, all keyed by the
 *      group/category/type trio: city pair, country, airport. Answers "is
 *      this kind of aircraft allowed here at all".
 *   2. Tail tier — the four limits on the individual row. Answers "and is
 *      THIS tail allowed", which the aircraft tier cannot: 22 of the 32 NEO
 *      non ACT tails are barred from Russia while the trio as a whole is not.
 *
 * Inside the aircraft tier the matrices are the baseline and sit lowest. An
 * ad-hoc exception, made against one city pair, country or airport, overrides
 * the baseline for the columns it names. Every verdict therefore reports
 * which layer decided it, so an exception can never be mistaken for the
 * standing rule.
 *
 * Rank is in neither tier. It orders candidates once both tiers have passed —
 * a rank 1 tail is not thereby permitted anywhere a rank 3 tail may go. The
 * shipped data makes that plain: the four rank 1 NEO ACT tails are barred
 * from Russia, where rank 2 tails are not.
 */

/** Matrix column key for an aircraft: the trio, joined. */
export function fleetColumnKey(row: { group: string; category: string; type: string }): string {
  return `${row.group}/${row.category}/${row.type}`
}

/**
 * Matrix columns for a fleet, one per distinct trio.
 *
 * Sorted by the trio itself rather than taken in fleet order, so the columns
 * do not reshuffle when a tail is added or renamed.
 */
export function fleetColumns(fleet: Aircraft[]): FleetColumn[] {
  const byKey = new Map<string, FleetColumn>()
  for (const row of fleet) {
    if (!row.type) continue
    const key = fleetColumnKey(row)
    const known = byKey.get(key)
    if (known) known.tails += 1
    else {
      byKey.set(key, {
        key,
        group: row.group,
        category: row.category,
        type: row.type,
        tails: 1,
      })
    }
  }
  return [...byKey.values()].sort(
    (a, b) =>
      a.group.localeCompare(b.group) ||
      a.category.localeCompare(b.category) ||
      a.type.localeCompare(b.type),
  )
}

/** Which layer a verdict came from. */
export type VerdictSource = 'exception' | 'rule' | 'group' | 'default' | 'none'

export interface Verdict {
  state: AllowState
  source: VerdictSource
  /** Group whose rule decided it, when `source` is 'group'. */
  group?: string
}

/**
 * Combines the group rules covering one place.
 *
 * A station can sit in several groups, so the verdicts have to be ordered
 * rather than picked: **deny beats allow**. Operating limits are the one
 * place where the safe reading is also the expected one — a group saying
 * "not here" is not overturned by a wider group saying "anywhere".
 *
 * A cell nobody set decides nothing and is skipped. In these two matrices
 * the absence of a rule already means "allowed", so letting a half-written
 * rule turn seven columns from allowed to undecided would take a planner
 * denying one aircraft at four stations and quietly ground the rest.
 */
function combineGroups(candidates: { state: AllowState; group: string }[]): Verdict | null {
  const denied = candidates.find((item) => item.state === 'no')
  if (denied) return { state: 'no', source: 'group', group: denied.group }
  const allowed = candidates.find((item) => item.state === 'yes')
  if (allowed) return { state: 'yes', source: 'group', group: allowed.group }
  return null
}

/** The exception standing against one subject, if there is one. */
export function findException(
  exceptions: CapabilityException[],
  scope: ExceptionScope,
  subject: string,
): CapabilityException | undefined {
  return exceptions.find((row) => row.scope === scope && row.subject === subject)
}

/**
 * Exceptions by subject, for a screen that resolves a whole table.
 *
 * A linear scan per row turns into a table scan per render on 8.672 airports,
 * so the caller builds the index once.
 */
export function exceptionIndex(
  exceptions: CapabilityException[],
  scope: ExceptionScope,
): Map<string, CapabilityException> {
  const out = new Map<string, CapabilityException>()
  for (const row of exceptions) if (row.scope === scope) out.set(row.subject, row)
  return out
}

/** An exception wins where it names the column, and only there. */
function withException(baseline: Verdict, exception: CapabilityException | undefined, column: string): Verdict {
  const state = exception?.allow[column]
  if (!state) return baseline
  return { state, source: 'exception' }
}

/**
 * Verdict for a city pair.
 *
 * A pair with no row of its own falls through to DEFAULT, which is what makes
 * the matrix usable while only the exceptional pairs are listed.
 */
export function resolveCityPair(
  rules: CityPairCapability[],
  exception: CapabilityException | undefined,
  cityPairKey: string,
  column: string,
): Verdict {
  const own = rules.find((rule) => rule.cityPairKey === cityPairKey)
  if (own) return withException({ state: own.allow[column] ?? 'unset', source: 'rule' }, exception, column)
  const fallback = rules.find((rule) => rule.cityPairKey === DEFAULT_RULE)
  const baseline: Verdict = fallback
    ? { state: fallback.allow[column] ?? 'unset', source: 'default' }
    : { state: 'unset', source: 'none' }
  return withException(baseline, exception, column)
}

/**
 * Verdict for a country. Silence means allowed.
 *
 * Four layers, most specific first: the ad-hoc exception, the country's own
 * rule, any group rule covering it, then silence. A rule written against one
 * country is not overruled by one written against its region, and a cell
 * nobody set is not a decision — it hands the question to the next layer
 * rather than denying by default.
 */
export function resolveCountry(
  rules: CountryCapability[],
  exception: CapabilityException | undefined,
  countryCode: string,
  column: string,
  groups?: Map<string, Set<string>>,
): Verdict {
  const own = rules.find(
    (rule) => rule.subjectKind === 'country' && rule.subject === countryCode,
  )
  const ownState = own?.allow[column]
  if (ownState && ownState !== 'unset') {
    return withException({ state: ownState, source: 'rule' }, exception, column)
  }

  const fromGroups = groups
    ? combineGroups(
        rules
          .filter(
            (rule) => rule.subjectKind === 'group' && groups.get(rule.subject)?.has(countryCode),
          )
          .map((rule) => ({ state: rule.allow[column] ?? 'unset', group: rule.subject })),
      )
    : null

  // No rule for the country at all: nothing restricts it.
  return withException(fromGroups ?? { state: 'yes', source: 'none' }, exception, column)
}

/** Verdict for a station. Same four layers as countries, same reasons. */
export function resolveAirport(
  rules: AirportCapability[],
  exception: CapabilityException | undefined,
  iata: string,
  column: string,
  groups?: Map<string, Set<string>>,
): Verdict {
  const own = rules.find((rule) => rule.subjectKind === 'station' && rule.subject === iata)
  const ownState = own?.allow[column]
  if (ownState && ownState !== 'unset') {
    return withException({ state: ownState, source: 'rule' }, exception, column)
  }

  const fromGroups = groups
    ? combineGroups(
        rules
          .filter((rule) => rule.subjectKind === 'group' && groups.get(rule.subject)?.has(iata))
          .map((rule) => ({ state: rule.allow[column] ?? 'unset', group: rule.subject })),
      )
    : null

  return withException(fromGroups ?? { state: 'yes', source: 'none' }, exception, column)
}

/**
 * The cells of an exception that actually differ from the baseline.
 *
 * An exception is a deviation, so a cell cycled back to the baseline stops
 * being one. Storing it anyway would put an "Exception" badge on a row that
 * follows the rule exactly, and that badge has to stay trustworthy.
 */
export function exceptionDiff(
  draft: AllowRow,
  baseline: (column: string) => AllowState,
  columns: FleetColumn[],
): AllowRow {
  const out: AllowRow = {}
  for (const column of columns) {
    const state = draft[column.key]
    if (state && state !== baseline(column.key)) out[column.key] = state
  }
  return out
}

export interface TailBlock {
  /** Which of the four limits refused, for the message the planner reads. */
  limit: 'country' | 'airport' | 'blockTime' | 'cycles'
  detail: string
  /** Group that matched, when the limit named one rather than a place. */
  group?: string
}

/**
 * Whether one entry of a tail limit covers a place.
 *
 * A limit entry is a bare string, so it may be a place code or a group code.
 * Group first: `LIMIT COUNTRY = ITL` on the ten wet-leased tails means
 * "everywhere international", which as a country code matches nothing at all.
 */
function limitHit(
  entries: string[],
  code: string,
  groups?: Map<string, Set<string>>,
): { entry: string; group?: string } | null {
  for (const entry of entries) {
    const members = groups?.get(entry)
    if (members) {
      if (members.has(code)) return { entry, group: entry }
      continue
    }
    if (entry === code) return { entry }
  }
  return null
}

/** Tail-tier check against a country, limit 3. */
export function tailBlockedInCountry(
  tail: Aircraft,
  countryCode: string,
  groups?: Map<string, Set<string>>,
): TailBlock | null {
  const hit = limitHit(tail.limitCountry, countryCode, groups)
  if (!hit) return null
  return { limit: 'country', detail: hit.entry, group: hit.group }
}

/** Tail-tier check against a station, limit 4. */
export function tailBlockedAtAirport(
  tail: Aircraft,
  iata: string,
  groups?: Map<string, Set<string>>,
): TailBlock | null {
  const hit = limitHit(tail.limitAirport, iata, groups)
  if (!hit) return null
  return { limit: 'airport', detail: hit.entry, group: hit.group }
}

/**
 * Tail-tier check against a leg's block time, limit 2.
 *
 * A ceiling: a tail carrying 120 may not work a leg longer than two hours.
 */
export function tailBlockedByBlockTime(tail: Aircraft, blockMinutes: number): TailBlock | null {
  if (!tail.maxBlockMinutes || blockMinutes <= tail.maxBlockMinutes) return null
  return { limit: 'blockTime', detail: `${tail.maxBlockMinutes} min` }
}

/** Tail-tier check against a day's rotation, limit 1. Also a ceiling. */
export function tailBlockedByCycles(tail: Aircraft, cyclesInDay: number): TailBlock | null {
  if (!tail.maxCyclesPerDay || cyclesInDay <= tail.maxCyclesPerDay) return null
  return { limit: 'cycles', detail: `${tail.maxCyclesPerDay}/day` }
}

/**
 * Tails a swap may draw on, best rank first.
 *
 * Rank 1 is the highest, so a candidate qualifies when its rank is at most
 * the rank of the tail coming off. This narrows the pool; it does not permit
 * anything — every candidate still goes through both tiers for the actual
 * legs, and through R5 for the actual passenger count.
 */
export function substitutionPool(tail: Aircraft, fleet: Aircraft[]): Aircraft[] {
  return fleet
    .filter((candidate) => candidate.regn !== tail.regn && candidate.rank <= tail.rank)
    .sort((a, b) => a.rank - b.rank || b.seats - a.seats || a.regn.localeCompare(b.regn))
}

/** Legs a city pair expands into, in the order they are flown. */
export function cityPairLegs(pair: CityPair): [string, string][] {
  const stations = pair.key.split('-')
  const legs: [string, string][] = []
  for (let index = 0; index < stations.length - 1; index += 1) {
    legs.push([stations[index], stations[index + 1]])
  }
  return legs
}

export interface VerdictCounts {
  yes: number
  no: number
  unset: number
  /** Of those, how many were decided by an exception rather than the rule. */
  exceptions: number
  /** Tails behind the permitted columns. */
  tails: number
}

/** How one subject's verdicts fall out across the fleet. */
export function verdictCounts(
  columns: FleetColumn[],
  verdict: (column: string) => Verdict,
): VerdictCounts {
  const counts: VerdictCounts = { yes: 0, no: 0, unset: 0, exceptions: 0, tails: 0 }
  for (const column of columns) {
    const { state, source } = verdict(column.key)
    counts[state] += 1
    if (source === 'exception') counts.exceptions += 1
    if (state === 'yes') counts.tails += column.tails
  }
  return counts
}
